import type { Bundle, Reference, Task } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedClientApplication } from '@seen/core/models/clientApplication'
import { isOfType } from '@seen/core/utils'

import { cn, DotSeparator, TypographyDetail } from '@seen/ui'
import type { JSX, ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Logo } from '#src/components/core/Logo.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'

interface TimelineTextEntryProps {
  actor: Reference | 'Unknown'
  actorIsLoading?: boolean
  action: ReactNode
  time: Date | string | null
  timeIsLoading?: boolean
  reference: Reference
  // The queued-edits review record this change originated from (a Task or the
  // proposal Bundle), when the resource carries the queued-edits origin tag.
  // Renders an inline "as part of … ·" backlink before the timestamp — a
  // hover-card for a Task, a static label for a Bundle. Derive from
  // `model.queuedEditsOriginTask`.
  originTask?: Reference<Task | Bundle>
  // Optional supporting content rendered on its own line beneath the
  // actor·action·time line, indented to align with the text (not the avatar).
  // Used to surface a note's AI summary preview under a visit-note entry.
  detail?: ReactNode
}

export function TimelineTextEntry({
  actor,
  actorIsLoading,
  action,
  time,
  timeIsLoading,
  reference,
  originTask,
  detail,
}: TimelineTextEntryProps): JSX.Element {
  // useWarningReporter(
  //   actor === 'Unknown' &&
  //     !actorIsLoading &&
  //     `Timeline entry actor is unknown for ${reference.reference}`
  // )
  useWarningReporter(
    actor !== 'Unknown' &&
      !actorIsLoading &&
      !isOfType(actor, 'Practitioner') &&
      !isOfType(actor, 'ClientApplication') &&
      !isOfType(actor, 'Organization') &&
      `Timeline entry actor is not a practitioner, client application, or organization for ${reference.reference}`
  )
  useWarningReporter(
    time === null &&
      !timeIsLoading &&
      `Timeline entry time is null for ${reference.reference}`
  )

  return (
    <div
      className={cn(
        'flex justify-between gap-2 py-1',
        'group overflow-hidden' // We add these classes to prevent the vertical line from the parent list from emanating below the last entry (see below for more).
      )}
    >
      <div className="flex w-full min-w-0 gap-2">
        <div
          className={cn([
            'ml-2.5',
            '-my-1.5 z-10 bg-background py-1.5', // Add visual margin between the avatars and the vertical line from the parent list. Design from linear.app.
            'group-last:mb-[-100rem] group-last:pb-[100rem]', // Prevent the vertical line from the parent list from emanating below the last entry.
          ])}
        >
          {actorIsLoading && <Avatar profile={undefined} size="icon" />}
          {!actorIsLoading && (
            <>
              {actor !== 'Unknown' && !isOfType(actor, 'ClientApplication') && (
                <Avatar profile={actor} size="icon" />
              )}

              {(actor === 'Unknown' ||
                isOfType(actor, 'ClientApplication')) && (
                <div className="flex size-5 items-center justify-center rounded-full bg-muted">
                  <Logo size={12} />
                </div>
              )}
            </>
          )}
        </div>
        <div
          className={cn(
            'flex w-full min-w-0 flex-col',
            'pt-0.5' // We want the content to line up with the avatar.
          )}
        >
          <TypographyDetail>
            {actorIsLoading && <HoverPractitioner practitioner={undefined} />}
            {!actorIsLoading && (
              <>
                {actor !== 'Unknown' && isOfType(actor, 'Practitioner') && (
                  <HoverPractitioner practitioner={actor} />
                )}
                {actor !== 'Unknown' &&
                  isOfType(actor, 'ClientApplication') &&
                  ExtendedClientApplication.companyName}
                {actor !== 'Unknown' &&
                  isOfType(actor, 'Organization') &&
                  referenceDisplay(actor)}
                {(actor === 'Unknown' ||
                  (!isOfType(actor, 'Practitioner') &&
                    !isOfType(actor, 'ClientApplication') &&
                    !isOfType(actor, 'Organization'))) &&
                  'Unknown'}
              </>
            )}{' '}
            <span className="font-normal text-foreground-secondary">
              {action && <>{action} </>}
              {originTask && (
                <>
                  <span className="text-muted-foreground">as part of</span>{' '}
                  {isOfType(originTask, 'Task') ? (
                    <HoverTask task={originTask} />
                  ) : (
                    <span>
                      {referenceDisplay(originTask) ?? 'an AI-proposed change'}
                    </span>
                  )}{' '}
                </>
              )}
              <DotSeparator />
              {!!time && <HoverDate date={time} format="datetime" />}
              {!time && 'Unknown time'}
            </span>
          </TypographyDetail>
          {detail}
        </div>
      </div>
    </div>
  )
}
